/**
 * 利用規約・プライバシーポリシーについて、ログイン中の利用者に何を求めるか
 *
 * - 同意（agree）: 同意するまで使えない（同意画面）。初めての同意・同意が要る改定・内容が分からない版
 * - 確認（acknowledge）: 止めずに改定を告知し、「内容を確認しました」で閉じる。確認するまで AI アシスタントだけ使えない
 * - なし: 今の版に同意・確認済み
 *
 * 今の版は API（GET /api/user/me の consentVersionStatus）、前回の同意・確認の版は consents の policyVersion、
 * 改定の中身は constants/policyRevisions.ts。版が API で上がっていても、その版の改定がここに無いとき
 * （画面の公開より先に版を上げたとき）は、内容を出せないので同意を求める側に倒す。
 */

import {
  POLICY_DOC,
  POLICY_NOTICE,
  POLICY_REVISIONS,
  type PolicyDoc,
  type PolicyRevision,
} from '@/constants/policyRevisions';

export const POLICY_ACTION = {
  NONE: 'none',
  ACKNOWLEDGE: 'acknowledge',
  AGREE: 'agree',
} as const;

export type PolicyAction = (typeof POLICY_ACTION)[keyof typeof POLICY_ACTION];

export interface PolicyConsentInput {
  consentType: string;
  granted: boolean;
  policyVersion?: string | null;
}

export interface PolicyVersionInput {
  termsPolicyVersion: string;
  privacyPolicyVersion: string;
}

export interface PolicyDocStatus {
  doc: PolicyDoc;
  action: PolicyAction;
  /** 今の版の施行日 */
  currentVersion: string;
  /** 初めての同意か（改定の告知ではない） */
  firstTime: boolean;
  /** 前回同意・確認した版の施行日（記録が無ければ undefined） */
  previousVersion?: string;
  /** 前回の同意・確認から今の版までの改定（新しい順）。初めての同意では空 */
  revisions: PolicyRevision[];
}

const DOCS: PolicyDoc[] = [POLICY_DOC.TERMS, POLICY_DOC.PRIVACY];

function evaluateDoc(
  doc: PolicyDoc,
  consent: PolicyConsentInput | undefined,
  currentVersion: string,
): PolicyDocStatus {
  const base = { doc, currentVersion, firstTime: false, revisions: [] as PolicyRevision[] };
  if (!consent) return { ...base, action: POLICY_ACTION.AGREE, firstTime: true };
  const previous = consent.policyVersion ?? '';
  if (previous === currentVersion) return { ...base, action: POLICY_ACTION.NONE };
  const previousVersion = previous || undefined;

  const all = POLICY_REVISIONS[doc];
  const known = all.some((revision) => revision.date === currentVersion);
  const revisions = all.filter(
    (revision) => revision.date <= currentVersion && (!previous || revision.date > previous),
  );
  const needsAgreement =
    !known || !previous || revisions.some((revision) => revision.notice === POLICY_NOTICE.AGREEMENT);
  return {
    ...base,
    revisions,
    previousVersion,
    action: needsAgreement ? POLICY_ACTION.AGREE : POLICY_ACTION.ACKNOWLEDGE,
  };
}

export function evaluatePolicies(
  consents: PolicyConsentInput[] | undefined,
  versions: PolicyVersionInput,
): PolicyDocStatus[] {
  const current: Record<PolicyDoc, string> = {
    [POLICY_DOC.TERMS]: versions.termsPolicyVersion,
    [POLICY_DOC.PRIVACY]: versions.privacyPolicyVersion,
  };
  return DOCS.map((doc) =>
    evaluateDoc(
      doc,
      consents?.find((consent) => consent.consentType === doc && consent.granted),
      current[doc],
    ),
  );
}
